'use client'

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Supplier } from '@garage/shared'
import { toWaMeDigits } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  TextField,
  Chip,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  AddIcon,
  EditIcon,
  DeleteIcon,
  WhatsappIcon,
  TruckIcon,
  StoreIcon,
  MapPinIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced } from '@/lib/hooks.js'
import { SupplierFormDialog } from '@/components/SupplierFormDialog.js'

/** Whether a location value is a pasted link (e.g. a Google Maps share URL) rather than a plain address. */
function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Where the workshop actually buys things. Search matches name, location,
 * and every tag (car brand or supplied category) — typing "bmw" surfaces
 * every supplier tagged for it, without a separate brand filter to maintain.
 */
export function SuppliersPage() {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounced(searchInput, 250)

  const [deleteSupplierId, setDeleteSupplierId] = useState<string | null>(null)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.suppliers({ q: debouncedSearch }),
    queryFn: () => api.suppliers.list({ q: debouncedSearch }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.suppliers.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.suppliers() })
      snackbar.success(t('supplierForm.supplierRemoved'))
      setDeleteSupplierId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('supplierForm.failedToRemove'))
    },
  })

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingSupplier(undefined)
  }

  const handleAddNew = () => {
    setEditingSupplier(undefined)
    setFormDialogOpen(true)
  }

  const items = data?.items ?? []
  const hasSearchQuery = debouncedSearch.trim() !== ''
  const isEmpty = items.length === 0

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('suppliers.title')}
        subtitle={t('suppliers.subtitle')}
        actions={
          <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
            {t('suppliers.addSupplier')}
          </Button>
        }
      />

      <TextField
        label={t('common.search')}
        value={searchInput}
        onChange={setSearchInput}
        placeholder={t('suppliers.searchPlaceholder')}
        fullWidth
      />

      {isLoading ? (
        <Skeleton lines={4} />
      ) : isError ? (
        <EmptyState title={t('suppliers.errorLoading')} description={t('clients.tryRefreshing')} />
      ) : isEmpty ? (
        hasSearchQuery ? (
          <EmptyState
            icon={<StoreIcon className="w-12 h-12 text-on-surface-variant" />}
            title={t('suppliers.noSuppliersFound')}
            description={t('suppliers.adjustSearch')}
          />
        ) : (
          <EmptyState
            icon={<StoreIcon className="w-12 h-12 text-on-surface-variant" />}
            title={t('suppliers.noSuppliersYet')}
            description={t('suppliers.addFirstSupplier')}
            action={
              <Button variant="tonal" onClick={handleAddNew}>
                {t('suppliers.addASupplier')}
              </Button>
            }
          />
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((supplier) => {
            const waDigits = supplier.phone ? toWaMeDigits(supplier.phone) : ''
            return (
              <div
                key={supplier.id}
                className="bg-surface-container border border-outline-variant rounded-lg p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-title-md text-on-surface truncate">{supplier.name}</div>
                    {supplier.location &&
                      (isHttpUrl(supplier.location) ? (
                        <a
                          href={supplier.location}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-body-sm text-primary hover:underline w-fit"
                        >
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{t('suppliers.viewLocation')}</span>
                        </a>
                      ) : (
                        <div className="text-body-sm text-on-surface-variant truncate">{supplier.location}</div>
                      ))}
                    {supplier.phone && <div className="ref text-body-sm text-on-surface-variant">{supplier.phone}</div>}
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    <IconButton
                      icon={<EditIcon className="w-4 h-4" />}
                      label={t('suppliers.editSupplier', { supplier: supplier.name })}
                      variant="standard"
                      size="sm"
                      onClick={() => handleEdit(supplier)}
                    />
                    <IconButton
                      icon={<DeleteIcon className="w-4 h-4" />}
                      label={t('suppliers.removeSupplier', { supplier: supplier.name })}
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteSupplierId(supplier.id)}
                    />
                  </div>
                </div>

                {supplier.offersDelivery && (
                  <div className="flex items-center gap-1.5 text-label-md text-success">
                    <TruckIcon className="w-4 h-4" />
                    <span>{t('suppliers.offersDelivery')}</span>
                  </div>
                )}

                {supplier.carBrands.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-label-sm text-on-surface-subtle uppercase tracking-wide">
                      {t('suppliers.carBrandsLabel')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {supplier.carBrands.map((brand) => (
                        <Chip key={brand} label={brand} />
                      ))}
                    </div>
                  </div>
                )}

                {supplier.suppliesCategories.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-label-sm text-on-surface-subtle uppercase tracking-wide">
                      {t('suppliers.suppliesLabel')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {supplier.suppliesCategories.map((category) => (
                        <Chip key={category} label={category} tone="secondary" />
                      ))}
                    </div>
                  </div>
                )}

                {supplier.notes && (
                  <p className="text-body-sm text-on-surface-variant">{supplier.notes}</p>
                )}

                {waDigits && (
                  <Button
                    variant="outlined"
                    size="sm"
                    icon={<WhatsappIcon className="w-4 h-4" />}
                    onClick={() => window.open(`https://wa.me/${waDigits}`, '_blank')}
                  >
                    {t('suppliers.messageOnWhatsapp')}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      )}

      <SupplierFormDialog open={formDialogOpen} supplier={editingSupplier} onClose={handleFormClose} />

      <ConfirmDialog
        open={!!deleteSupplierId}
        title={t('suppliers.removeSupplierTitle')}
        message={t('suppliers.removeSupplierMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteSupplierId) deleteMutation.mutate(deleteSupplierId)
        }}
        onCancel={() => setDeleteSupplierId(null)}
      />
    </div>
  )
}
